import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Plus, Send, Clock, CheckCircle2, AlertTriangle,
  MessageSquare, X, Paperclip, Loader2, ChevronRight, Tag, User
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';

const CATEGORIES = [
  { value: 'billing', color: 'bg-purple-100 text-purple-700' },
  { value: 'technical', color: 'bg-blue-100 text-blue-700' },
  { value: 'account', color: 'bg-amber-100 text-amber-700' },
  { value: 'general', color: 'bg-slate-100 text-slate-700' },
];

const PRIORITIES = [
  { value: 'low', color: 'bg-slate-100 text-slate-600' },
  { value: 'medium', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', color: 'bg-orange-100 text-orange-700' },
  { value: 'urgent', color: 'bg-red-100 text-red-700' },
];

const STATUS_STYLES = {
  open: 'bg-green-100 text-green-700',
  in_progress: 'bg-blue-100 text-blue-700',
  waiting: 'bg-amber-100 text-amber-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-slate-100 text-slate-500',
};

const TicketsPage = () => {
  const { api } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  // Create form
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('medium');
  const [message, setMessage] = useState('');

  const fetchTickets = useCallback(async () => {
    try {
      const res = await api.get('/tickets');
      setTickets(res.data);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return;
    setSending(true);
    try {
      await api.post('/tickets', { subject, category, priority, message });
      setShowCreate(false);
      setSubject(''); setCategory('general'); setPriority('medium'); setMessage('');
      fetchTickets();
    } catch (err) {
      console.error('Failed to create ticket:', err);
    }
    setSending(false);
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    setSending(true);
    try {
      const res = await api.post(`/tickets/${selectedTicket.id}/reply`, { message: replyText });
      setSelectedTicket(res.data);
      setReplyText('');
      fetchTickets();
    } catch (err) {
      console.error('Failed to reply:', err);
    }
    setSending(false);
  };

  const handleClose = async (ticketId) => {
    try {
      await api.post(`/tickets/${ticketId}/close`);
      setSelectedTicket(null);
      fetchTickets();
    } catch (err) {
      console.error('Failed to close ticket:', err);
    }
  };

  const getCatStyle = (cat) => CATEGORIES.find(c => c.value === cat)?.color || '';
  const getPrioStyle = (p) => PRIORITIES.find(pr => pr.value === p)?.color || '';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="tickets-page">
      {/* Header */}
      <header className="bg-white border-b-2 border-slate-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/shop/dashboard')} className="p-2 hover:bg-slate-100 rounded-sm">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#1e3a5f]">{t('tickets_title')}</h1>
              <p className="text-sm text-slate-500">{t('tickets_subtitle')}</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="vf-btn-primary flex items-center gap-2"
            data-testid="create-ticket-btn"
          >
            <Plus className="w-4 h-4" />
            {t('tickets_new')}
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6">
        {tickets.length === 0 ? (
          <div className="text-center py-16">
            <MessageSquare className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h2 className="text-xl font-bold text-[#1e3a5f] mb-2">{t('tickets_empty')}</h2>
            <p className="text-slate-500 mb-6">{t('tickets_empty_desc')}</p>
            <button onClick={() => setShowCreate(true)} className="vf-btn-primary">
              <Plus className="w-4 h-4 inline mr-2" />{t('tickets_new')}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map(ticket => (
              <motion.div
                key={ticket.id}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white border-2 border-slate-100 p-4 hover:border-slate-200 cursor-pointer transition-all"
                onClick={() => setSelectedTicket(ticket)}
                data-testid={`ticket-${ticket.ticket_number}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-mono text-slate-400">{ticket.ticket_number}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[ticket.status]}`}>
                        {ticket.status.replace('_', ' ')}
                      </span>
                    </div>
                    <h3 className="font-semibold text-[#1e3a5f] truncate">{ticket.subject}</h3>
                    <div className="flex items-center gap-3 mt-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getCatStyle(ticket.category)}`}>
                        {ticket.category}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getPrioStyle(ticket.priority)}`}>
                        {ticket.priority}
                      </span>
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" />
                        {ticket.messages?.length || 0}
                      </span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-slate-400">{new Date(ticket.created_at).toLocaleDateString()}</p>
                    <ChevronRight className="w-5 h-5 text-slate-300 mt-2 ml-auto" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Create Ticket Modal */}
      <AnimatePresence>
        {showCreate && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowCreate(false)}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
              className="w-full md:w-[560px] bg-white rounded-sm flex flex-col max-h-[90vh]"
              data-testid="create-ticket-modal"
              onClick={e => e.stopPropagation()}>
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-lg font-bold text-[#1e3a5f]">{t('tickets_new')}</h3>
                <button onClick={() => setShowCreate(false)} className="p-1 hover:bg-slate-100 rounded-sm">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleCreate} className="flex-1 overflow-y-auto p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('tickets_subject')}</label>
                  <input type="text" value={subject} onChange={e => setSubject(e.target.value)} required
                    className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                    data-testid="ticket-subject-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('tickets_category')}</label>
                    <select value={category} onChange={e => setCategory(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                      data-testid="ticket-category-select">
                      {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.value}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('tickets_priority')}</label>
                    <select value={priority} onChange={e => setPriority(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                      data-testid="ticket-priority-select">
                      {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.value}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('tickets_message')}</label>
                  <textarea value={message} onChange={e => setMessage(e.target.value)} required rows={5}
                    className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none resize-none"
                    data-testid="ticket-message-input" />
                </div>
                <button type="submit" disabled={sending} className="vf-btn-primary w-full flex items-center justify-center gap-2" data-testid="ticket-submit-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {t('tickets_submit')}
                </button>
              </form>
            </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Ticket Detail Modal */}
      <AnimatePresence>
        {selectedTicket && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50" onClick={() => setSelectedTicket(null)} />
            <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 40 }}
              className="fixed right-0 top-0 bottom-0 w-full md:w-[600px] bg-white z-50 flex flex-col shadow-2xl"
              data-testid="ticket-detail-panel">
              {/* Detail Header */}
              <div className="p-4 border-b border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-mono text-slate-400">{selectedTicket.ticket_number}</span>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[selectedTicket.status]}`}>
                      {selectedTicket.status.replace('_', ' ')}
                    </span>
                    <button onClick={() => setSelectedTicket(null)} className="p-1 hover:bg-slate-100 rounded-sm">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-[#1e3a5f]">{selectedTicket.subject}</h3>
                <div className="flex items-center gap-3 mt-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${getCatStyle(selectedTicket.category)}`}>
                    <Tag className="w-3 h-3 inline mr-1" />{selectedTicket.category}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${getPrioStyle(selectedTicket.priority)}`}>
                    <AlertTriangle className="w-3 h-3 inline mr-1" />{selectedTicket.priority}
                  </span>
                  <span className="text-xs text-slate-400">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {new Date(selectedTicket.created_at).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {selectedTicket.messages?.map(msg => (
                  <div key={msg.id} className={`flex ${msg.sender === 'admin' ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[80%] p-3 rounded-sm ${
                      msg.sender === 'admin'
                        ? 'bg-blue-50 border border-blue-100'
                        : 'bg-slate-50 border border-slate-200'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-3 h-3 text-slate-400" />
                        <span className="text-xs font-medium text-slate-600">{msg.sender_name}</span>
                        <span className="text-xs text-slate-400">{new Date(msg.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm text-slate-800 whitespace-pre-wrap">{msg.message}</p>
                      {msg.attachments?.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {msg.attachments.map(att => (
                            <a key={att.id} href={att.url} target="_blank" rel="noreferrer"
                              className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                              <Paperclip className="w-3 h-3" />{att.filename}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply Box */}
              {selectedTicket.status !== 'closed' ? (
                <div className="p-4 border-t border-slate-200 bg-slate-50">
                  <div className="flex gap-2">
                    <input
                      type="text" value={replyText} onChange={e => setReplyText(e.target.value)}
                      placeholder={t('tickets_reply_placeholder')}
                      className="flex-1 px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                      onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleReply()}
                      data-testid="ticket-reply-input"
                    />
                    <button onClick={handleReply} disabled={sending || !replyText.trim()}
                      className="vf-btn-primary px-4 disabled:opacity-50" data-testid="ticket-reply-btn">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                  </div>
                  <button onClick={() => handleClose(selectedTicket.id)}
                    className="mt-2 text-xs text-slate-400 hover:text-red-500"
                    data-testid="ticket-close-btn">
                    <CheckCircle2 className="w-3 h-3 inline mr-1" />{t('tickets_close')}
                  </button>
                </div>
              ) : (
                <div className="p-4 border-t border-slate-200 bg-slate-100 text-center">
                  <p className="text-sm text-slate-500">{t('tickets_closed_msg')}</p>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TicketsPage;
